import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import type { ScheduleConfig, SequenceStep, Weekday } from './types.js';
import { WEEKDAYS } from './types.js';

const TIMEZONES = [
  { value: 'America/New_York', label: 'America / New York' },
  { value: 'America/Chicago', label: 'America / Chicago' },
  { value: 'America/Denver', label: 'America / Denver' },
  { value: 'America/Los_Angeles', label: 'America / Los Angeles' },
  { value: 'Europe/London', label: 'Europe / London' },
  { value: 'Europe/Berlin', label: 'Europe / Berlin, Paris' },
  { value: 'Europe/Helsinki', label: 'Europe / Helsinki, Athens' },
  { value: 'Europe/Istanbul', label: 'Europe / Istanbul' },
  { value: 'Asia/Dubai', label: 'Asia / Dubai' },
  { value: 'Asia/Singapore', label: 'Asia / Singapore' },
  { value: 'Asia/Tokyo', label: 'Asia / Tokyo' },
  { value: 'Australia/Sydney', label: 'Australia / Sydney' },
  { value: 'UTC', label: 'UTC' },
];

export interface WizScheduleProps {
  schedule: ScheduleConfig;
  sequence: SequenceStep[];
  onScheduleChange: (schedule: ScheduleConfig) => void;
}

export const WizSchedule = stateful((props: WizScheduleProps) => {
  const { schedule, sequence, onScheduleChange } = props;
  // The schedule step adapts itself to whichever channels the sequence uses.
  // • If LinkedIn is part of the sequence, we show the LinkedIn-specific
  //   business-hours warning and the fixed daily-limit notice.
  // • If only LinkedIn is used, the daily-limit input is hidden entirely.
  const hasLinkedin = sequence.some(s => s.channel === 'linkedin_connect' || s.channel === 'linkedin_dm');
  const hasEmail = sequence.some(s => s.channel === 'email');
  const linkedinOnly = hasLinkedin && !hasEmail;

  const update = (patch: Partial<ScheduleConfig>) => {
    onScheduleChange({ ...schedule, ...patch });
  };

  const toggleDay = (weekday: Weekday) => {
    const days = schedule.sendDays.some(d => d.day === weekday.day)
      ? schedule.sendDays.filter(d => d.day !== weekday.day)
      : [...schedule.sendDays, weekday].sort((a, b) => a.day - b.day);
    update({ sendDays: days });
  };

  const selectedTz = TIMEZONES.find(tz => tz.value === schedule.timezone) ?? TIMEZONES[0]!;

  return html`
    <div class="flex flex-col gap-6">
      <!-- Sending Window -->
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body flex flex-col gap-5">
          <div class="flex items-center gap-3">
            <span class="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--schedule] text-xl"></span>
            </span>
            <div>
              <h2 class="card-title">Schedule & Timing</h2>
              <p class="text-base-content/70 text-sm">
                Configure when messages are sent, the timezone, and daily limits.
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium">Timezone</span>
            ${DropdownSelectionInput<(typeof TIMEZONES)[number]>({
              value: selectedTz,
              selectables: TIMEZONES,
              renderString: tz => tz.label,
              resolveID: tz => tz.value,
              onPicked: tz => {
                if (tz) update({ timezone: tz.value });
              },
              placeholder: 'Select timezone…',
            })}
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium">Start Time</span>
              ${DateInput({
                type: 'time',
                value: schedule.startTime,
                onPicked: d => {
                  if (d) update({ startTime: d });
                },
              })}
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium">End Time</span>
              ${DateInput({
                type: 'time',
                value: schedule.endTime,
                onPicked: d => {
                  if (d) update({ endTime: d });
                },
              })}
            </div>
          </div>

          ${
            hasLinkedin &&
            (
              () => {
                const startH = schedule.startTime.getHours();
                const endH = schedule.endTime.getHours();
                return startH < 9 || endH > 17
                  ? html`
              <div class="alert alert-warning py-2 text-sm">
                <span class="icon-[material-symbols--warning-outline] text-base"></span>
                <span>Sending outside business hours (9–17) can increase the chance of your LinkedIn sender getting blocked.</span>
              </div>
            `
                  : null;
              }
            )()
          }

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium">Send Days</span>
            <div class="flex flex-wrap gap-2">
              ${WEEKDAYS.map(
                weekday => html`
                  <button
                    class="btn btn-sm ${
                      schedule.sendDays.some(d => d.day === weekday.day) ? 'btn-primary' : 'btn-outline'
                    }"
                    @click=${() => toggleDay(weekday)}
                  >
                    ${weekday.displayName}
                  </button>
                `,
              )}
            </div>
          </div>

          ${
            linkedinOnly
              ? html`
            <div class="alert alert-info py-2 text-sm">
              <span class="icon-[material-symbols--info-outline] text-base"></span>
              <span>Daily limit set to 20 connection requests due to LinkedIn restrictions.</span>
            </div>
          `
              : html`
            <div class="flex flex-col gap-1" style="max-width: 200px">
              <span class="text-sm font-medium">Daily Limit</span>
              ${NumberInput({
                value: schedule.dailyLimit,
                onPicked: value => update({ dailyLimit: value ?? 30 }),
                min: 1,
                max: 500,
                placeholder: '30',
              })}
            </div>
          `
          }
        </div>
      </div>
    </div>
  `;
});
